/**
 * Supabase DB helpers — replaces src/api/firestore.ts
 * Provides typed CRUD wrappers and the TABLES constant.
 */

import { supabase } from '../supabase';

export { supabase };

// ============================================================================
// TABLE NAMES (mirrors COLLECTIONS from the old firestore.ts)
// ============================================================================

export const TABLES = {
  USERS: 'users',
  TASKS: 'tasks',
  TASK_HATCH_SUBMISSIONS: 'task_hatch_submissions',
  AI_ANALYSES: 'ai_analyses',
  NEURAL_TASKS: 'neural_tasks',
  TASKER_PROFILES: 'tasker_profiles',
  TASKER_DASHBOARD_DATA: 'tasker_dashboard_data',
  CONNECTION_REQUESTS: 'connection_requests',
  TASK_PARTICIPANTS: 'task_participants',
  TASK_TIMELINE_ENTRIES: 'task_timeline_entries',
  TASK_CHATS: 'task_chats',
  CHAT_MESSAGES: 'chat_messages',
  NOTIFICATIONS: 'notifications',
  USER_SETTINGS: 'user_settings',
  WORKSPACE_ITEMS: 'workspace_items',
  PROMPT_POSTS: 'prompt_posts',
  TASK_RATINGS: 'task_ratings',
} as const;

export type TableName = typeof TABLES[keyof typeof TABLES];

// ============================================================================
// SNAKE ↔ CAMEL CASE MAPPERS
// ============================================================================

/** Convert a snake_case DB row object to camelCase for TypeScript types. */
export function fromDb<T = Record<string, unknown>>(row: Record<string, unknown> | null): T {
  if (!row) return {} as T;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(row)) {
    const camel = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
    out[camel] = row[key];
  }
  return out as T;
}

/** Convert a camelCase TS object to snake_case for Supabase insert/update. */
export function toDb(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const snake = key.replace(/([A-Z])/g, (c) => `_${c.toLowerCase()}`);
    out[snake] = obj[key];
  }
  return out;
}

/** Map an array of DB rows to camelCase TS objects and attach the row `id`. */
export function fromDbList<T>(rows: Array<Record<string, unknown>>): T[] {
  return rows.map((r) => fromDb<T>(r));
}

// ============================================================================
// GENERIC CRUD HELPERS
// ============================================================================

/**
 * Fetch a single row by ID.
 * Returns null when not found.
 */
export async function getDocument<T>(
  table: TableName,
  id: string
): Promise<T | null> {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return fromDb<T>(data as Record<string, unknown>);
}

/**
 * Fetch multiple rows, optionally applying simple equality filters.
 */
export async function getDocuments<T>(
  table: TableName,
  filters?: Record<string, unknown>,
  orderByCol?: string,
  ascending = false,
  limitN?: number
): Promise<T[]> {
  let q = supabase.from(table).select('*');

  if (filters) {
    for (const [col, val] of Object.entries(filters)) {
      q = q.eq(col, val as string);
    }
  }

  if (orderByCol) {
    q = q.order(orderByCol, { ascending });
  }

  if (limitN) {
    q = q.limit(limitN);
  }

  const { data, error } = await q;
  if (error) throw error;
  return fromDbList<T>((data as Array<Record<string, unknown>>) ?? []);
}

/**
 * Insert a new row (without a preset ID — DB generates UUID).
 * Returns the inserted row's ID.
 */
export async function createDocument<T>(
  table: TableName,
  data: Omit<T, 'id'>,
  customId?: string
): Promise<string> {
  const payload = toDb(data as Record<string, unknown>);
  if (customId) payload['id'] = customId;

  const { data: inserted, error } = await supabase
    .from(table)
    .insert(payload)
    .select('id')
    .single();

  if (error) throw error;
  return (inserted as { id: string }).id;
}

/**
 * Update a row by ID (partial merge).
 */
export async function updateDocument<T>(
  table: TableName,
  id: string,
  updates: Partial<T>
): Promise<void> {
  const payload = toDb(updates as Record<string, unknown>);
  const { error } = await supabase.from(table).update(payload).eq('id', id);
  if (error) throw error;
}

/**
 * Delete a row by ID.
 */
export async function deleteDocument(table: TableName, id: string): Promise<void> {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

/**
 * Upsert a row (insert or update on conflict).
 */
export async function upsertDocument<T>(
  table: TableName,
  data: T,
  onConflict?: string
): Promise<void> {
  const payload = toDb(data as Record<string, unknown>);
  const opts = onConflict ? { onConflict } : {};
  const { error } = await supabase.from(table).upsert(payload, opts);
  if (error) throw error;
}
